import { OverflowText } from "@verity/components/core";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import i18n from "../../../../../i18n.js";
import CorrectIcon from "../../../../../images/quiz-question/option-correct-answer-checked-icon.svg?react";
import InCorrectIcon from "../../../../../images/quiz-question/option-inccorect-icon.svg?react";
import AiCorrectStatusIcon from "../../../../../images/quiz-question/question-status-correct-ai-icon.svg?react";
import CorrectStatusIcon from "../../../../../images/quiz-question/question-status-correct-icon.svg?react";
import AiIncorrectStatusIcon from "../../../../../images/quiz-question/question-status-incorrect-ai-icon.svg?react";
import IncorrectStatusIcon from "../../../../../images/quiz-question/question-status-incorrect-icon.svg?react";
import AiPartialStatusIcon from "../../../../../images/quiz-question/question-status-partial-correct-ai-icon.svg?react";
import PartialStatusIcon from "../../../../../images/quiz-question/question-status-partial-correct-icon.svg?react";
import UnansweredStatusIcon from "../../../../../images/quiz-question/question-status-unanswered-icon.svg?react";
import UngradedStatusIcon from "../../../../../images/quiz-question/question-status-ungraded-icon.svg?react";
import AudioRecordingIcon from "../../../../../images/quiz-question/question-type-icon-audio-recording.svg?react";
import CodingIcon from "../../../../../images/quiz-question/question-type-icon-coding.svg?react";
import DragAndDropIcon from "../../../../../images/quiz-question/question-type-icon-drag-and-drop.svg?react";
import DropdownIcon from "../../../../../images/quiz-question/question-type-icon-dropdown.svg?react";
import EssayIcon from "../../../../../images/quiz-question/question-type-icon-essay.svg?react";
import FileSubmissionIcon from "../../../../../images/quiz-question/question-type-icon-file-submission.svg?react";
import FillInTheBlankIcon from "../../../../../images/quiz-question/question-type-icon-fill-in-the-blank.svg?react";
import MatchingIcon from "../../../../../images/quiz-question/question-type-icon-matching.svg?react";
import MultipleChoiceIcon from "../../../../../images/quiz-question/question-type-icon-multiple-choice.svg?react";
import OrderingIcon from "../../../../../images/quiz-question/question-type-icon-ordering.svg?react";
import SpreadsheetIcon from "../../../../../images/quiz-question/question-type-icon-spreadsheet.svg?react";
import TrueFalseIcon from "../../../../../images/quiz-question/question-type-icon-true-false.svg?react";
import { QuizQuestion, QuizResponseResult } from "../hooks/verityAssessmentResultHelper.js";
import styles from "./QuestionAnswer.module.css";

export type AnswerViewProps = {
	question: QuizQuestion;
	response: QuizResponseResult | undefined;
};

/** Correct-answer renderers never need the student's response, only the question. */
export type CorrectAnswerViewProps = {
	question: QuizQuestion;
};

/** Shared i18n lookup, namespaced for this feature. */
export function t(key: string, opts?: Record<string, unknown>): string {
	return i18n.t(`result.components.resultsPage.proctoringResultsViewTSX.questions.${key}`, opts) as string;
}

export type ScoreStatus = "correct" | "partial" | "incorrect" | "ungraded" | "unanswered";

export function getScoreStatus(scored: number, total: number, isAnswered: boolean, isUngraded: boolean): ScoreStatus {
	if (!isAnswered) return "unanswered";
	if (isUngraded) return "ungraded";
	if (scored >= total) return "correct";
	if (scored > 0) return "partial";
	return "incorrect";
}

export function getStatusTooltip(status: ScoreStatus): string {
	return i18n.t(`result.components.resultsPage.proctoringResultsViewTSX.questions.statusTooltip.${status}`) as string;
}

export function StatusIcon({ status, isAiGraded }: { status: ScoreStatus; isAiGraded: boolean }) {
	switch (status) {
		case "correct":
			return isAiGraded ? <AiCorrectStatusIcon aria-hidden="true" /> : <CorrectStatusIcon aria-hidden="true" />;
		case "partial":
			return isAiGraded ? <AiPartialStatusIcon aria-hidden="true" /> : <PartialStatusIcon aria-hidden="true" />;
		case "incorrect":
			return isAiGraded ? <AiIncorrectStatusIcon aria-hidden="true" /> : <IncorrectStatusIcon aria-hidden="true" />;
		case "ungraded":
			return <UngradedStatusIcon aria-hidden="true" />;
		case "unanswered":
			return <UnansweredStatusIcon aria-hidden="true" />;
	}
}

export function QuestionTypeIcon({ type }: { type: QuizQuestion["questionType"] }) {
	switch (type) {
		case "MultipleChoice":
			return <MultipleChoiceIcon aria-hidden="true" />;
		case "TrueFalse":
			return <TrueFalseIcon aria-hidden="true" />;
		case "FillInTheBlank":
			return <FillInTheBlankIcon aria-hidden="true" />;
		case "Matching":
			return <MatchingIcon aria-hidden="true" />;
		case "Essay":
			return <EssayIcon aria-hidden="true" />;
		case "Ordering":
			return <OrderingIcon aria-hidden="true" />;
		case "AudioRecording":
			return <AudioRecordingIcon aria-hidden="true" />;
		case "FileSubmission":
			return <FileSubmissionIcon aria-hidden="true" />;
		case "DragAndDrop":
			return <DragAndDropIcon aria-hidden="true" />;
		case "Dropdown":
			return <DropdownIcon aria-hidden="true" />;
		case "Spreadsheet":
			return <SpreadsheetIcon aria-hidden="true" />;
		case "Coding":
			return <CodingIcon aria-hidden="true" />;
	}
}

// ── Blank-embed segment parser — shared by FillInTheBlank/Dropdown/DragAndDrop ──────────────────

export type BlankSegment = { type: "html"; content: string } | { type: "blank"; blankId: number };

export function parseBlankSegments(html: string): BlankSegment[] {
	if (typeof document === "undefined") return [{ type: "html", content: html }];
	const div = document.createElement("div");
	div.innerHTML = DOMPurify.sanitize(html, { ADD_ATTR: ["data-id"], ADD_TAGS: ["span"] });
	const PLACEHOLDER = "__FITB_BLANK_";
	div.querySelectorAll(".question-blank").forEach((span) => {
		const blankId = span.getAttribute("data-id") ?? "0";
		span.replaceWith(document.createTextNode(`${PLACEHOLDER}${blankId}__`));
	});
	const result: BlankSegment[] = [];
	const root: Element =
		div.children.length === 1 && div.firstElementChild?.tagName === "P" ? (div.firstElementChild as HTMLElement) : div;
	let remaining = root.innerHTML;
	const regex = new RegExp(`${PLACEHOLDER}(\\d+)__`);
	while (remaining.length > 0) {
		const match = regex.exec(remaining);
		if (!match) {
			result.push({ type: "html", content: remaining });
			break;
		}
		if (match.index > 0) result.push({ type: "html", content: remaining.slice(0, match.index) });
		result.push({ type: "blank", blankId: parseInt(match[1]) });
		remaining = remaining.slice(match.index + match[0].length);
	}
	return result;
}

/** Renders question text with blanks as "______" placeholders. */
export function BlankQuestionText({ html }: { html: string }) {
	const segments = useMemo(() => parseBlankSegments(html), [html]);
	return (
		<div className={styles.blankInlineWrapper}>
			{segments.map((seg, i) =>
				seg.type === "html" ? (
					<span key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seg.content) }} />
				) : (
					<span key={i} className={styles.blankInlinePlaceholder}>
						______
					</span>
				),
			)}
		</div>
	);
}

/** `isCorrect: null` means unanswered — used per-blank and standalone for manually-scanned types. */
export function AnswerStatusChip({
	userText,
	isCorrect,
	...props
}: { userText: string; isCorrect: boolean | null } & React.HTMLAttributes<HTMLSpanElement>) {
	const isUnanswered = isCorrect === null;
	const statusClass = isUnanswered ? "" : isCorrect ? styles.correct : styles.incorrect;
	return (
		<span className={`${styles.blankInlineChip} ${statusClass}`} {...props}>
			<span className={`${styles.blankInlineChipIcon} ${statusClass}`}>
				{isUnanswered ? (
					<UnansweredStatusIcon style={{ width: "14px" }} />
				) : isCorrect ? (
					<CorrectIcon />
				) : (
					<InCorrectIcon />
				)}
			</span>
			<OverflowText
				className={styles.blankInlineChipText}
				variant="body2"
				fontWeight={400}
				fontSize="14px"
				style={{ maxWidth: "100px", padding: "0" }}
			>
				{userText || t("unanswered")}
			</OverflowText>
		</span>
	);
}

/** Strips the upload-time uniqueness suffix from a stored file name, for display.
 * Duplicated from take-assessment's deriveDisplayName() to avoid a cross-feature import. */
export function deriveFileDisplayName(fileName: string): string {
	return fileName.replace(/-\d+(\.[^./]+)?$/, "$1");
}
